<template>
  <PermissionsTable
    :rows="permissions"
    :active="(row, _) => route?.params?.id?.toString() === row.id"
    :loading="isLoading"
    :page="currentPage"
    :searchEnabled="true"
    :searchQuery="searchQuery"
    :has-more-items="hasNextPage"
    :showGrantPermission="true"
    @load="fetchNextPage"
    @search="search($event)"
    @grant-permission="openGrantPermissionModal"
    :grant-permission-permission-id="tenantPermissionPermissions.GRANT_PERMISSIONS"
  >
    <template #grant-permission-modal>
      <GrantPermissionModal
        v-if="isGrantPermissionModalOpen"
        :permissions="allPermissions"
        :enabled-permissions="enabledPermissions"
        :loading="isLoading || isLoadingAllPermissions"
        :is-granting="isGranting"
        v-model:search-query="searchAllPermissionsQuery"
        @close="closeGrantPermissionModal"
        @add="addPermission"
        @remove="removePermission"
        @clear="clearCategory"
        @readOnly="readOnlyCategory"
        @all="allCategory"
        @save="grantPermissions"
      />
    </template>
  </PermissionsTable>
</template>

<script setup lang="ts">
import { AccessType } from '@infuse/api/iam/permission';
import { Tenant, tenantPermissionPermissions } from '@infuse/api/iam/tenant';
import { usePermissions } from '@infuse/iam/permission/queries';
import { GrantPermissionModal, PermissionsTable } from '@infuse/iam/permission/ui';
import { useGrantPermissionToTenant } from '@infuse/iam/tenant/mutations';
import { useTenantPermissions } from '@infuse/iam/tenant/queries';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

defineProps<{
  model: Tenant;
}>();

defineEmits(['reloadModel']);

const { fetchParentId, permissions, currentPage, isLoading, hasNextPage, fetchNextPage, searchQuery } =
  useTenantPermissions();
const {
  permissions: allPermissions,
  searchQuery: searchAllPermissionsQuery,
  isLoading: isLoadingAllPermissions
} = usePermissions();

const router = useRouter();
const route = useRoute();

watch(
  route,
  () => {
    fetchParentId.value = route.params.id as string;
  },
  { immediate: true }
);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'tenant.permissions') {
    return;
  }
  router.replace({
    name: 'tenant.permissions',
    query: {
      search: searchQuery || undefined
    }
  });
}

// Grant Permission Modal State
const isGrantPermissionModalOpen = ref(false);
const enabledPermissions = ref<string[]>([]);

watch(
  permissions,
  () => {
    // If the modal is open, the user is likely changing the permissions, so we don't want to reset the enabled permissions
    if (isGrantPermissionModalOpen.value) {
      return;
    }
    enabledPermissions.value = permissions.value.map((p) => p.id);
  },
  { immediate: true }
);

function openGrantPermissionModal() {
  isGrantPermissionModalOpen.value = true;
}

function closeGrantPermissionModal() {
  isGrantPermissionModalOpen.value = false;
}

function addPermission(id: string) {
  if (!enabledPermissions.value.includes(id)) {
    enabledPermissions.value.push(id);
  }
}

function removePermission(id: string) {
  const idx = enabledPermissions.value.indexOf(id);
  if (idx !== -1) {
    enabledPermissions.value.splice(idx, 1);
  }
}

function clearCategory(categoryId: string) {
  for (const perm of allPermissions.value.filter((p) => p.category.id === categoryId)) {
    removePermission(perm.id);
  }
}

function readOnlyCategory(categoryId: string) {
  clearCategory(categoryId);
  for (const perm of allPermissions.value.filter(
    (p) => p.category.id === categoryId && (p.accessType === AccessType.READ || p.accessType === AccessType.SEARCH)
  )) {
    addPermission(perm.id);
  }
}

function allCategory(categoryId: string) {
  for (const perm of allPermissions.value.filter((p) => p.category.id === categoryId)) {
    addPermission(perm.id);
  }
}

const { mutateAsync: grantPermissionMutation, isPending: isGranting } = useGrantPermissionToTenant();

async function grantPermissions() {
  await grantPermissionMutation({
    parentId: route.params.id as string,
    permissionIds: enabledPermissions.value,
    replace: true
  });
  closeGrantPermissionModal();
}
</script>
